/**
 * conversationsSelectors — la bandeja de conversaciones, derivada.
 *
 * No hay "modelo de conversación" nuevo. Los mensajes viven donde siempre
 * (`whatsapp_messages`, uno por lead, que se lee desde la ficha del lead) y el
 * lead ya lleva los marcadores que el backend estampa en cada mensaje real:
 *
 *   last_wa_inbound_at      cada entrante de verdad
 *   last_message_sent_at    cada saliente humano CONFIRMADO
 *   last_wa_inbound_ack_at  "marcar como atendido" del entrante actual
 *   last_message_channel    por dónde salió el último mensaje
 *   wa_unread_count         sin leer, que NO es lo mismo que sin contestar
 *   last_wa_preview         marcador SIN contenido ("[text]", "[image]")
 *
 * Esta lista compone esos campos y nada más: cero peticiones nuevas, cero
 * colecciones nuevas, cero proveedores nuevos. Abrir una conversación es
 * abrir la del lead, en su ficha, que es donde está el hilo entero.
 *
 * Frontera de privacidad: `last_wa_preview` es deliberadamente un marcador de
 * tipo, no un extracto. El documento del lead viaja entero a admin y a
 * marketing, y las conversaciones son de admin: por eso el texto del mensaje
 * nunca se ha guardado ahí, y aquí tampoco se finge que exista.
 */
import { whatsappWindowOpen, leadConversationHref } from "@/lib/leadDetailAnchors";

const parse = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * ¿Está esperando nuestra respuesta?
 *
 * MISMA regla que el Centro de acción aplica en el backend
 * (`actions_engine._actions_from_followups`): el entrante es más reciente que
 * el último saliente confirmado Y que el último "marcar como atendido". Si esa
 * regla cambiara, cambia allí primero — aquí solo se representa.
 *
 * Nunca se deriva de `wa_unread_count`: abrir una conversación no es
 * contestarla, y usar el contador haría desaparecer el aviso con solo mirarlo.
 */
export function awaitingReply(lead) {
  const inbound = parse(lead?.last_wa_inbound_at);
  if (inbound === null) return false;
  const answered = parse(lead?.last_message_sent_at);
  const acked = parse(lead?.last_wa_inbound_ack_at);
  return (answered === null || inbound > answered)
    && (acked === null || inbound > acked);
}

/** Un lead tiene conversación si ha entrado o ha salido un WhatsApp real. */
export function hasConversation(lead) {
  if (lead?.last_wa_inbound_at) return true;
  return Boolean(lead?.last_message_sent_at)
    && (lead?.last_message_channel || "").toLowerCase() === "whatsapp";
}

/**
 * Una fila de la bandeja. Todo derivado del lead; nada declarado.
 *
 * `last_activity_at` es lo más reciente de las dos puntas del hilo, que es lo
 * que ordena una bandeja. `direction` dice de quién fue la última palabra,
 * porque es lo que decide si hay algo que hacer.
 */
export function conversationRow(lead, now = Date.now()) {
  const inbound = parse(lead.last_wa_inbound_at);
  const outbound = parse(lead.last_message_sent_at);
  const lastIsInbound = inbound !== null && (outbound === null || inbound >= outbound);
  return {
    lead_id: lead.lead_id,
    name: lead.name || null,
    status: lead.status || null,
    assigned_to_name: lead.assigned_to_name || null,
    unread: Number(lead.wa_unread_count) || 0,
    last_inbound_at: lead.last_wa_inbound_at || null,
    last_outbound_at: lead.last_message_sent_at || null,
    last_activity_at: lastIsInbound ? lead.last_wa_inbound_at : lead.last_message_sent_at || null,
    direction: lastIsInbound ? "in" : "out",
    // Marcador de tipo, no texto: el contenido no vive en el lead.
    preview: lead.last_wa_preview || null,
    awaiting: awaitingReply(lead),
    // La ventana de 24h la decide Meta; esto es la misma heurística de
    // visualización que usa la conversación, y el backend sigue mandando.
    window_open: whatsappWindowOpen(lead.last_wa_inbound_at, now),
    href: leadConversationHref(lead.lead_id),
  };
}

/**
 * La bandeja: los leads con conversación, primero quien espera respuesta y
 * después por actividad. Los archivados no salen, igual que en Leads.
 */
export function conversationRowsFrom(leads, now = Date.now()) {
  return (leads || [])
    .filter((l) => l && !l.archived && hasConversation(l))
    .map((l) => conversationRow(l, now))
    .sort((a, b) => {
      if (a.awaiting !== b.awaiting) return a.awaiting ? -1 : 1;
      return String(b.last_activity_at || "").localeCompare(String(a.last_activity_at || ""));
    });
}

/** Cuántas esperan respuesta — el único número que la cabecera necesita. */
export function awaitingCount(rows) {
  return (rows || []).filter((r) => r.awaiting).length;
}

/** "[image]" → "Imagen". Sin contenido: solo qué tipo de mensaje fue. */
const PREVIEW_LABEL = {
  text: "Mensaje de texto",
  image: "Imagen",
  video: "Vídeo",
  audio: "Audio",
  voice: "Nota de voz",
  document: "Documento",
  sticker: "Sticker",
  location: "Ubicación",
  contacts: "Contacto",
  template: "Plantilla",
};

export function previewLabel(preview) {
  if (!preview) return null;
  const key = String(preview).replace(/^\[|\]$/g, "").toLowerCase();
  return PREVIEW_LABEL[key] || "Mensaje";
}
